import { useHeatmapAnalyticClickType, useHeatmapLoadingContext } from '~/modules/heatmap/hooks';
import { GInfoPanelPopover, GSkeletonDisplayText } from '~/presentation/common';
import { EClickType, useHeatmapSettingContext } from '@gem-sdk/heatmap-react';
import { HEATMAP_CLICK_CONFIG } from '~/modules/heatmap/configs';
import { EHeatmapLoadingKey } from '~/modules/heatmap/types';
import { BlockStack, Box, Text } from '@shopify/polaris';
import { useCallback, useEffect, useMemo } from 'react';
import { useFeatureFlagChecker } from '~/modules/apps';
import { useTranslation } from 'react-i18next';
import { SPACING_INSET } from '../Sidebar';

const SelectClickTypeSkeleton: React.FC = () => (
  <Box paddingBlock="300" paddingBlockEnd="200">
    <div style={{ width: '100%' }}>
      <GSkeletonDisplayText height="12px" />
    </div>
  </Box>
);

export const SelectClickType: React.FC = () => {
  const isLoading = useHeatmapLoadingContext((s) =>
    s.getLoadingByKey(EHeatmapLoadingKey.ANALYTICS),
  );
  const setClickType = useHeatmapSettingContext((state) => state.setClickType);
  const clickType = useHeatmapSettingContext((state) => state.clickType);

  const { t } = useTranslation();
  const { getCountByClickType } = useHeatmapAnalyticClickType();
  const { isEnabledHeatmapV2 } = useFeatureFlagChecker();

  const handleSelectChange = useCallback(
    (value: string) => setClickType(value as EClickType),
    [setClickType],
  );

  const items = useMemo(
    () => [
      {
        id: EClickType.All,
        label: t('Total clicks • {{count}}', { count: getCountByClickType(EClickType.All) }),
        data: {
          title: t('Total clicks'),
          description: t('Track all clicks or taps on the page'),
        },
      },
      {
        id: EClickType.Rage,
        label: t('Rage clicks • {{count}}', { count: getCountByClickType(EClickType.Rage) }),
        data: {
          title: t('Rage clicks'),
          description: t(
            'Tracks repeated clicks or taps within the same area, indicating frustration',
          ),
        },
      },
      {
        id: EClickType.Dead,
        label: t('Dead clicks • {{count}}', { count: getCountByClickType(EClickType.Dead) }),
        data: {
          title: t('Dead clicks'),
          description: t('Tracks clicks or taps on a page that have no effect'),
        },
      },
      {
        id: EClickType.Error,
        label: t('Error clicks • {{count}}', { count: getCountByClickType(EClickType.Error) }),
        data: {
          title: t('Error clicks'),
          description: t('Tracks clicks or taps that occur immediately before JavaScript errors'),
        },
      },
      {
        id: EClickType.First,
        label: t('First clicks • {{count}}', { count: getCountByClickType(EClickType.First) }),
        data: {
          title: t('First clicks'),
          description: t('Track first click actions on the page'),
        },
      },
      {
        id: EClickType.Last,
        label: t('Last clicks • {{count}}', { count: getCountByClickType(EClickType.Last) }),
        data: {
          title: t('Last clicks'),
          description: t('Track final click actions on the page'),
        },
      },
    ],
    [t, getCountByClickType],
  );

  useEffect(() => {
    if (clickType) return;

    setClickType(HEATMAP_CLICK_CONFIG.CLICK_TYPE);
  }, [clickType, setClickType]);

  return (
    <Box padding={isEnabledHeatmapV2 ? '0' : SPACING_INSET}>
      <BlockStack gap={SPACING_INSET}>
        <Text as="span" variant="bodyMd" fontWeight="medium">
          {t('Click type')}
        </Text>

        {isLoading ? (
          <SelectClickTypeSkeleton />
        ) : (
          <GInfoPanelPopover
            items={items}
            selectedId={clickType}
            onSelect={handleSelectChange}
            fullWidth
            minWidth="458px"
            columns="236px 1fr"
            defaultInfo={{
              title: t('Select a click type'),
              description: t(
                'Choose a click type to analyze different user interaction patterns on your page.',
              ),
            }}
          />
        )}
      </BlockStack>
    </Box>
  );
};
